import React, { Dispatch, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DialogHeader from '../_parts/DialogHeader';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../redux/store';
import { useEnqueueSnackbar } from '../../../../hooks/useEnqueueSnackbar';
import { ToastSeverity } from '../../Toast/Toast';
import DialogFrame from '../_parts/DialogFrame';
import { updateSelectedDocuments } from '../../../../redux/project/documents/sliceProjectSelectedDocuments';
import { useParams } from 'react-router-dom';
import { fetchViewDataAndDocuments } from '../../../../util/ProjectUtils';
import {
    fetchImportDocTypes,
    fetchImportViewData,
    fetchImportViews,
} from '../../../../util/ImportUtils';
import { DialogContent, FormControl, FormLabel, SxProps, TextField } from '@mui/material';
import { ContentLayout } from '../_parts/ContentLayout';
import Documents from '../_parts/Documents';
import { removeProjectDocumentsInfoItem } from '../../../../redux/project/documents/sliceProjectDocuments';
import { closeReleaseLetterDialog, resetError } from '../../../../redux/project/documents/sliceProjectDocumentAddReleaseLetter';
import { headingFontBold, bodyFont, vars } from '../../../../Theme';
import ReleaseLetterDialogActions from './_parts/ReleaseLetterDialogActions';
import Message from '../_parts/Message';
import { mdiAlertCircle } from '@mdi/js';

const formLabelSx: SxProps = {
    ...headingFontBold,
    fontSize: '16px',
    color: vars.black,
    fontWeight: 700,
    marginBottom: '2px',

    '&.Mui-focused': {
        color: vars.black,
    },
};

const textFieldSx: SxProps = {
    input: { ...bodyFont, p: 0 },

    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
            border: `2px solid ${vars.black}`,
        },
    },
    '& .MuiFormHelperText-root': {
        marginLeft: 'unset',
    },

    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
        py: '6.5px',
        pl: '12px',
    },
};

const initialMainState: IReleaseLetterMainState = {
    text: 'Keine zusätzliche Ergänzung erforderlich',
};

const initialErrorState: IReleaseLetterErrorState = {
    text: false,
};

export default () => {
    const { t } = useTranslation('translations', { keyPrefix: 'releaseLetterDialog' });
    const dispatch: Dispatch<unknown> = useDispatch();
    const enqueueSnackbar = useEnqueueSnackbar();
    const params = useParams();
    const { areaName, replicaId } = params;

    const { data: documents, loading: loadingInfoDocs } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocuments.info,
    );
    const { isDialogOpen, isRequestSent, error } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocumentsAddReleaseLetter,
    );

    const [mainState, setMainState] = useState<IReleaseLetterMainState>(initialMainState);
    const [errorState, setErrorState] = useState<IReleaseLetterErrorState>(initialErrorState);

    useEffect(() => {
        if (isRequestSent) {
            enqueueSnackbar(t('toastInfo', { numberOfDocuments: documents.length }), {
                severity: ToastSeverity.Info,
            });
            dispatch(
                updateSelectedDocuments({
                    replicaId,
                    areaName,
                    documentIds: [],
                }),
            );
            if (areaName === 'import') {
                fetchImportViews(replicaId);
                fetchImportViewData(replicaId);
                fetchImportDocTypes(replicaId);
            } else {
                fetchViewDataAndDocuments(params);
            }

            onClose();
        }
    }, [isRequestSent]);

    useEffect(() => {
        if (error) {
            enqueueSnackbar(t('toastError'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetError);
        }
    }, [error]);

    const hasError = useMemo(() => {
        return Object.keys(errorState).some(field => Boolean(errorState[field]));
    }, [errorState]);

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        const isValid = Boolean(newValue.trim());

        setErrorState({ ...errorState, text: !isValid });
        setMainState(prevState => ({
            ...prevState,
            text: newValue,
        }));
    };

    const onClose = () => {
        setErrorState(initialErrorState);
        setMainState(initialMainState);
        dispatch(closeReleaseLetterDialog());
    };

    const removeDocument = (universalId: string) => {
        dispatch(removeProjectDocumentsInfoItem({ universalId }));
    };

    return (
        <DialogFrame open={isDialogOpen}>
            <DialogHeader onClose={onClose} dialogTitle={t('dialogTitle')} />
            <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
                <ContentLayout
                    loading={loadingInfoDocs}
                    documentSelection={
                        <Documents removeDocument={removeDocument} documents={documents} />
                    }
                    main={
                        <FormControl>
                            <FormLabel sx={formLabelSx} required>
                                {t('description')}
                            </FormLabel>
                            <TextField
                                size='small'
                                sx={textFieldSx}
                                onChange={onInputChange}
                                placeholder={t('enterText')}
                                value={mainState.text}
                                multiline
                                minRows={3}
                                error={errorState.text}
                                helperText={
                                    errorState.text && (
                                        <Message
                                            text={t('errorInput')}
                                            icon={mdiAlertCircle}
                                            iconSize={0.75}
                                            color={vars.errorDialogRed}
                                            typographySx={{
                                                fontWeight: 400,
                                                fontSize: '12px',
                                                ...bodyFont,
                                            }}
                                            wrapperSx={{
                                                border: 'none',
                                                p: 0,
                                                m: 0,
                                                gap: 0.5,
                                            }}
                                        />
                                    )
                                }
                            />
                        </FormControl>
                    }
                />
            </DialogContent>
            <ReleaseLetterDialogActions
                mainState={mainState}
                hasError={hasError}
                onClose={onClose}
            />
        </DialogFrame>
    );
};

export interface IReleaseLetterMainState {
    text: string;
}

export interface IReleaseLetterErrorState {
    text: boolean;
}
